import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { GuestsResponse, StatsResponse } from '../types/guest';

export function useGuestsQuery(params: {
  search: string;
  field: string;
  page: number;
  limit: number;
  pay_date?: string;
  registered?: string;
  dinner?: string;
}) {
  return useQuery<GuestsResponse>({
    queryKey: ['guests', params],
    queryFn: () => api.guests.list(params) as Promise<GuestsResponse>,
    placeholderData: keepPreviousData,
    refetchInterval: 5_000,
    staleTime: 3_000,
  });
}

export function useStatsQuery() {
  return useQuery<StatsResponse>({
    queryKey: ['stats'],
    queryFn: () => api.stats.get() as Promise<StatsResponse>,
    refetchInterval: 5_000,
    staleTime: 3_000,
  });
}

export function useGuestMutations() {
  const queryClient = useQueryClient();

  const bulkUpdate = useMutation({
    mutationFn: (updates: { id: number; field: string; value: boolean }[]) =>
      api.guests.bulkUpdate(updates),
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: ['guests'] });
      const previous = queryClient.getQueriesData({ queryKey: ['guests'] });

      queryClient.setQueriesData({ queryKey: ['guests'] }, (old: GuestsResponse | undefined) => {
        if (!old) return old;
        return {
          ...old,
          guests: old.guests.map((g) => {
            const upd = updates.find((u) => u.id === g.id);
            return upd ? { ...g, [upd.field]: upd.value } : g;
          }),
        };
      });

      return { previous };
    },
    onError: (_err, _updates, context) => {
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
  });

  const deleteGuest = useMutation({
    mutationFn: (id: number) => api.guests.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
  });

  const createGuest = useMutation({
    mutationFn: (data: { l_name: string; f_name: string; p_name?: string; company?: string; role?: string; country?: string; email?: string }) =>
      api.guests.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
  });

  const updateGuest = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { l_name: string; f_name: string; p_name?: string; company?: string; role?: string; country?: string; email?: string } }) =>
      api.guests.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
    },
  });

  return { bulkUpdate, deleteGuest, createGuest, updateGuest };
}
